/**
 * Adlib panels and actions from the production's studio config snapshot
 * (D22): the panels' buttons and the free adlib's piece-type choices. The
 * param form they open is `paramForm.ts`.
 *
 * The snapshot is parsed with the engine's schema (`parseProductionConfig`) once,
 * in the view; everything here reads the parsed config.
 */
import type { ProductionConfig } from '@acp/engine/productionConfig.js';
import type { JsonValue } from '@acp/domain/primitives.js';

export type AdlibItemView =
  | {
      readonly kind: 'piece';
      readonly label: string;
      readonly pieceType: string;
      readonly pieceTypeLabel: string;
      readonly params: Readonly<Record<string, JsonValue>>;
      readonly mode: 'now' | 'queue';
      readonly hotkey: string | null;
      /** The piece type has params to fill in (the item's own are the form's starting values). */
      readonly hasParams: boolean;
      /** The config names a piece type the snapshot lacks: shown, and disabled. */
      readonly missing: boolean;
    }
  | { readonly kind: 'action'; readonly label: string; readonly actionId: string; readonly hotkey: string | null; readonly missing: boolean };

export type AdlibPanelView = { readonly id: string; readonly label: string; readonly items: readonly AdlibItemView[] };

export type ActionView = { readonly id: string; readonly label: string; readonly steps: number };

export function adlibPanelsOf(config: ProductionConfig): AdlibPanelView[] {
  return config.adlibPanels.map((panel) => ({
    id: panel.id,
    label: panel.label === '' ? panel.id : panel.label,
    items: panel.items.map((item): AdlibItemView => {
      if ('action' in item) return { kind: 'action', label: item.label, actionId: item.action, hotkey: item.hotkey, missing: !Object.hasOwn(config.actions, item.action) };
      const type = config.pieceTypes[item.pieceType];
      return {
        kind: 'piece',
        label: item.label,
        pieceType: item.pieceType,
        pieceTypeLabel: type === undefined || type.label === '' ? item.pieceType : type.label,
        params: item.params,
        mode: item.mode,
        hotkey: item.hotkey,
        hasParams: type !== undefined && Object.keys(type.params).length > 0,
        missing: type === undefined,
      };
    }),
  }));
}

export function actionsOf(config: ProductionConfig): ActionView[] {
  return Object.entries(config.actions).map(([id, a]) => ({ id, label: a.label === '' ? id : a.label, steps: a.steps.length }));
}

/** Every piece type, for the free adlib form (any type, any params). */
export function pieceTypeChoices(config: ProductionConfig): { readonly id: string; readonly label: string }[] {
  return Object.entries(config.pieceTypes).map(([id, t]) => ({ id, label: t.label === '' ? id : t.label }));
}
